import { Bookmark, BookmarkPlus, ChevronLeft, ChevronRight, Plus, Search as SearchIcon, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Button } from "../actions/button";
import { Checkbox } from "../components/arc/checkbox/checkbox";
import {
  type DateRange,
  DateRangePicker,
  type DateRangePreset,
} from "../components/arc/date-range-picker/date-range-picker";
import { Input } from "../components/arc/input/input";
import { Popover, PopoverContent, PopoverTrigger } from "../components/arc/popover/popover";
import { SearchField } from "../components/arc/search-field/search-field";
import { type CompanyFormat, dayRange } from "../format/format";
import styles from "./filter-bar.module.css";
import {
  activeFields,
  chipText,
  clearAll,
  clearOf,
  type FilterField,
  type FilterOperator,
  type ListViewDef,
  type OptionsValue,
  readOptions,
  type Search,
  writeOptions,
} from "./filters";

type Patch = (patch: Search) => void;

const OPERATORS: [FilterOperator, string][] = [
  ["any", "is any of"],
  ["none", "is none of"],
  ["empty", "is empty"],
  ["set", "is set"],
];

/** Pick values, and how they match (any of, none of, empty, set). */
function OptionsEditor({
  field,
  search,
  onApply,
  onCancel,
}: {
  field: Extract<FilterField, { kind: "options" }>;
  search: Search;
  onApply: Patch;
  onCancel: () => void;
}) {
  const start = readOptions(field.param, search) ?? { op: "any" as const, values: [] };
  const [value, setValue] = useState<OptionsValue>(start);
  const [needle, setNeedle] = useState("");
  const ops = OPERATORS.filter(([op]) => field.nullable || op === "any" || op === "none");
  const picking = value.op === "any" || value.op === "none";
  const shown = field.options.filter((o) => o.label.toLowerCase().includes(needle.trim().toLowerCase()));
  const toggle = (v: string, on: boolean) =>
    setValue((s) => ({ ...s, values: on ? [...s.values, v] : s.values.filter((x) => x !== v) }));
  const ready = !picking || value.values.length > 0;
  return (
    <div className={styles.editor}>
      <div className={styles.editorHead}>
        <span className={styles.editorTitle}>
          {field.icon}
          {field.label}
        </span>
        <div className={styles.ops} role="radiogroup" aria-label="Match">
          {ops.map(([op, label]) => (
            // biome-ignore lint/a11y/useSemanticElements: a pill row reads better than radio inputs here
            <button
              key={op}
              type="button"
              role="radio"
              aria-checked={value.op === op}
              className={styles.op}
              data-on={value.op === op}
              onClick={() => setValue((s) => ({ op, values: op === "empty" || op === "set" ? [] : s.values }))}
            >
              {label}
            </button>
          ))}
        </div>
        {picking && field.options.length > 7 ? (
          <label className={styles.find}>
            <SearchIcon size={14} aria-hidden />
            <input
              value={needle}
              onChange={(e) => setNeedle(e.target.value)}
              placeholder={`Find ${field.label.toLowerCase()}`}
              aria-label={`Find ${field.label.toLowerCase()}`}
            />
          </label>
        ) : null}
      </div>
      {picking ? (
        <ul className={styles.options}>
          {shown.length === 0 ? <li className={styles.none}>Nothing matches.</li> : null}
          {shown.map((o) => (
            <li key={o.value}>
              <Checkbox
                label={o.label}
                description={o.hint}
                checked={value.values.includes(o.value)}
                onCheckedChange={(on) => toggle(o.value, on === true)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.explain}>
          {value.op === "empty"
            ? `Only those with ${field.emptyLabel ? field.emptyLabel.toLowerCase() : `no ${field.label.toLowerCase()}`}.`
            : `Only those with a ${field.label.toLowerCase()}.`}
        </p>
      )}
      <div className={styles.editorFoot}>
        <span className={styles.muted}>{picking ? `${value.values.length} selected` : ""}</span>
        <span className={styles.buttons}>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" disabled={!ready} onClick={() => onApply(writeOptions(field.param, value))}>
            Apply
          </Button>
        </span>
      </div>
    </div>
  );
}

/** The company-zone calendar day of an instant, as a local Date for the picker. */
const pickerDayOf = (iso: string | undefined, timezone: string, end = false): Date | null => {
  if (!iso) return null;
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(
    new Date(Date.parse(iso) - (end ? 1 : 0)),
  );
  // number-ok: calendar parts of a date, not money
  const [y, m, d] = day.split("-").map((n) => Number.parseInt(n, 10));
  return new Date(y, m - 1, d);
};

const plusDays = (day: Date, days: number) => new Date(day.getFullYear(), day.getMonth(), day.getDate() + days);

/** The usual ranges, a click away in the picker. */
const DATE_PRESETS: DateRangePreset[] = [
  { label: "Today", range: (today) => ({ start: today, end: today }) },
  { label: "Tomorrow", range: (today) => ({ start: plusDays(today, 1), end: plusDays(today, 1) }) },
  { label: "Next 7 days", range: (today) => ({ start: today, end: plusDays(today, 6) }) },
  { label: "Last 7 days", range: (today) => ({ start: plusDays(today, -6), end: today }) },
  { label: "Last 30 days", range: (today) => ({ start: plusDays(today, -29), end: today }) },
];

/** Whole days in the company's time zone. The picker's own Apply sets the filter. */
function DatesEditor({
  field,
  search,
  format,
  onApply,
}: {
  field: Extract<FilterField, { kind: "dates" }>;
  search: Search;
  format: CompanyFormat;
  onApply: Patch;
}) {
  const start = pickerDayOf(search[field.from], format.timezone);
  const end = pickerDayOf(search[field.to], format.timezone, true);
  return (
    <div className={styles.editor}>
      <div className={styles.editorHead}>
        <span className={styles.editorTitle}>
          {field.icon}
          {field.label}
        </span>
      </div>
      <div className={styles.dates}>
        <DateRangePicker
          label="Days"
          placeholder="Choose days"
          value={start && end ? { start, end } : null}
          onChange={(range: DateRange) => {
            const days = dayRange(range, format.timezone);
            onApply({ [field.from]: days.from, [field.to]: days.to });
          }}
          presets={DATE_PRESETS}
          weekStartsOn={1}
          locale="en-GB"
          months={1}
        />
      </div>
      <p className={styles.muted}>In {format.timezone.replace("_", " ")} time</p>
    </div>
  );
}

const AMOUNT = /^\d{1,12}(\.\d{1,2})?$/;

/** A money range: at least, at most, or both. */
function AmountEditor({
  field,
  search,
  format,
  onApply,
  onCancel,
}: {
  field: Extract<FilterField, { kind: "amount" }>;
  search: Search;
  format: CompanyFormat;
  onApply: Patch;
  onCancel: () => void;
}) {
  const [min, setMin] = useState(search[field.min] ?? "");
  const [max, setMax] = useState(search[field.max] ?? "");
  const ok = (v: string) => v === "" || AMOUNT.test(v);
  const ready = (min !== "" || max !== "") && ok(min) && ok(max);
  return (
    <div className={styles.editor}>
      <div className={styles.editorHead}>
        <span className={styles.editorTitle}>
          {field.icon}
          {field.label}
        </span>
      </div>
      <div className={styles.range}>
        <Input
          label={`At least (${format.currency})`}
          inputMode="decimal"
          value={min}
          onChange={(e) => setMin(e.target.value.trim())}
          placeholder="0.00"
          error={ok(min) ? undefined : "Like 5000.50"}
        />
        <Input
          label={`At most (${format.currency})`}
          inputMode="decimal"
          value={max}
          onChange={(e) => setMax(e.target.value.trim())}
          placeholder="Any"
          error={ok(max) ? undefined : "Like 5000.50"}
        />
      </div>
      <div className={styles.editorFoot}>
        <span />
        <span className={styles.buttons}>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!ready}
            onClick={() => onApply({ [field.min]: min || undefined, [field.max]: max || undefined })}
          >
            Apply
          </Button>
        </span>
      </div>
    </div>
  );
}

function Editor(props: {
  field: FilterField;
  search: Search;
  format: CompanyFormat;
  onApply: Patch;
  onCancel: () => void;
}) {
  const { field } = props;
  if (field.kind === "options") return <OptionsEditor {...props} field={field} />;
  if (field.kind === "dates") return <DatesEditor {...props} field={field} />;
  if (field.kind === "amount") return <AmountEditor {...props} field={field} />;
  return null;
}

/** A set filter: what it says, a way to change it, and a way to take it off. */
function Chip({
  field,
  search,
  format,
  onChange,
  onEdit,
}: {
  field: FilterField;
  search: Search;
  format: CompanyFormat;
  onChange: Patch;
  onEdit?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const text = chipText(field, search, format);
  if (!text) return null;
  const editable = field.kind === "options" || field.kind === "dates" || field.kind === "amount";
  const label = (
    <>
      <span className={styles.chipField}>{field.label}</span>
      {text.operator ? <span className={styles.chipOp}>{text.operator}</span> : null}
      {text.value ? <span className={styles.chipValue}>{text.value}</span> : null}
    </>
  );
  return (
    <span className={styles.chip}>
      {editable ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger className={styles.chipBody} aria-label={`Change ${field.label} filter`}>
            {label}
          </PopoverTrigger>
          <PopoverContent className={styles.panel} align="start">
            <Editor
              field={field}
              search={search}
              format={format}
              onApply={(patch) => {
                onChange(patch);
                setOpen(false);
              }}
              onCancel={() => setOpen(false)}
            />
          </PopoverContent>
        </Popover>
      ) : field.kind === "custom" && onEdit ? (
        <button type="button" className={styles.chipBody} onClick={onEdit}>
          {label}
        </button>
      ) : (
        <span className={styles.chipBody}>{label}</span>
      )}
      <button
        type="button"
        className={styles.remove}
        aria-label={`Remove ${field.label} filter`}
        onClick={() => onChange(clearOf(field))}
      >
        <X size={13} aria-hidden />
      </button>
    </span>
  );
}

/** Add filter: every field by group, then the chosen field's editor in the same panel. */
function AddFilter({
  fields,
  search,
  format,
  onChange,
  onCustom,
}: {
  fields: FilterField[];
  search: Search;
  format: CompanyFormat;
  onChange: Patch;
  onCustom?: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<FilterField | null>(null);
  const [needle, setNeedle] = useState("");
  const groups = useMemo(() => {
    const out = new Map<string, FilterField[]>();
    const n = needle.trim().toLowerCase();
    for (const f of fields) {
      if (n && !f.label.toLowerCase().includes(n)) continue;
      out.set(f.group, [...(out.get(f.group) ?? []), f]);
    }
    return [...out.entries()];
  }, [fields, needle]);
  const close = () => {
    setOpen(false);
    setChosen(null);
    setNeedle("");
  };
  const pick = (f: FilterField) => {
    if (f.kind === "flag") {
      onChange({ [f.param]: f.value });
      close();
    } else if (f.kind === "custom") {
      close();
      onCustom?.(f.id);
    } else setChosen(f);
  };
  return (
    <Popover open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <PopoverTrigger className={styles.add}>
        <Plus size={14} aria-hidden /> Add filter
      </PopoverTrigger>
      <PopoverContent className={styles.panel} align="start">
        {chosen ? (
          <>
            <button type="button" className={styles.back} onClick={() => setChosen(null)}>
              <ChevronLeft size={14} aria-hidden /> All filters
            </button>
            <Editor
              field={chosen}
              search={search}
              format={format}
              onApply={(patch) => {
                onChange(patch);
                close();
              }}
              onCancel={close}
            />
          </>
        ) : (
          <div className={styles.menu}>
            <label className={styles.find}>
              <SearchIcon size={14} aria-hidden />
              <input
                value={needle}
                onChange={(e) => setNeedle(e.target.value)}
                placeholder="Filter by…"
                aria-label="Find a filter"
              />
            </label>
            {groups.map(([group, list]) => (
              <div key={group} className={styles.group}>
                <span className={styles.groupLabel}>{group}</span>
                {list.map((f) => (
                  <button key={f.id} type="button" className={styles.field} onClick={() => pick(f)}>
                    <span className={styles.fieldIcon}>{f.icon}</span>
                    <span className={styles.fieldLabel}>{f.label}</span>
                    {chipText(f, search, format) ? <span className={styles.muted}>In use</span> : null}
                    {f.kind === "flag" || f.kind === "custom" ? null : <ChevronRight size={14} aria-hidden />}
                  </button>
                ))}
              </div>
            ))}
            {groups.length === 0 ? <p className={styles.none}>No filter by that name.</p> : null}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export interface FilterBarProps {
  fields: FilterField[];
  /** The URL's search params. */
  search: Search;
  onChange: Patch;
  format: CompanyFormat;
  /** The free-text search: its param and what it finds. */
  query?: { param: string; placeholder: string };
  /** Sort, columns and the like, at the end of the search row. */
  actions?: ReactNode;
  /** A field of kind "custom" was chosen (open its picker). */
  onCustom?: (id: string) => void;
}

/**
 * A list's filters (P8-57, `Console / Orders / Redesign`): a search, then every filter that is set
 * as a chip (field · operator · values), Add filter and Clear all. Everything lives in the URL.
 */
export function FilterBar({ fields, search, onChange, format, query, actions, onCustom }: FilterBarProps) {
  const [text, setText] = useState(query ? (search[query.param] ?? "") : "");
  const current = query ? (search[query.param] ?? "") : "";
  useEffect(() => setText(current), [current]);
  useEffect(() => {
    if (!query || text.trim() === current) return;
    const t = setTimeout(() => onChange({ [query.param]: text.trim() || undefined }), 300);
    return () => clearTimeout(t);
  }, [text, current, query, onChange]);
  const set = activeFields(fields, search);
  return (
    <div className={styles.bar}>
      {query || actions ? (
        <div className={styles.searchRow}>
          {query ? (
            <div className={styles.search}>
              <SearchField label="Search" value={text} onValueChange={setText} placeholder={query.placeholder} />
            </div>
          ) : null}
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </div>
      ) : null}
      <div className={styles.chips}>
        {set.map((f) => (
          <Chip
            key={f.id}
            field={f}
            search={search}
            format={format}
            onChange={onChange}
            onEdit={onCustom ? () => onCustom(f.id) : undefined}
          />
        ))}
        <AddFilter fields={fields} search={search} format={format} onChange={onChange} onCustom={onCustom} />
        {set.length ? (
          <button type="button" className={styles.clear} onClick={() => onChange(clearAll(fields))}>
            Clear all
          </button>
        ) : null}
      </div>
    </div>
  );
}

export interface ViewTabsProps {
  views: (ListViewDef & { count?: number })[];
  current?: string;
  onPick: (view: ListViewDef) => void;
  /** Save the filters on screen as a view, asking for its name. */
  onSave?: (label: string) => void;
  onDelete?: (view: ListViewDef) => void;
}

/** A list's views (P8-57): built-in and saved, each a set of filters, and Save view. */
export function ViewTabs({ views, current, onPick, onSave, onDelete }: ViewTabsProps) {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const save = () => {
    if (!name.trim() || !onSave) return;
    onSave(name.trim());
    setName("");
    setNaming(false);
  };
  return (
    <div className={styles.views}>
      <div className={styles.tabs} role="tablist" aria-label="Views">
        {views.map((v) => (
          <span key={v.id} className={styles.tabWrap}>
            <button
              type="button"
              role="tab"
              aria-selected={current === v.id}
              className={styles.tab}
              data-on={current === v.id}
              onClick={() => onPick(v)}
            >
              {v.saved ? <Bookmark size={13} aria-hidden /> : null}
              {v.label}
              {v.count !== undefined ? <span className={styles.count}>{v.count.toLocaleString("en-GB")}</span> : null}
            </button>
            {v.saved && onDelete ? (
              <button
                type="button"
                className={styles.tabRemove}
                aria-label={`Delete view ${v.label}`}
                onClick={() => onDelete(v)}
              >
                <X size={12} aria-hidden />
              </button>
            ) : null}
          </span>
        ))}
      </div>
      {onSave ? (
        <Popover open={naming} onOpenChange={setNaming}>
          <PopoverTrigger className={styles.saveView}>
            <BookmarkPlus size={15} aria-hidden /> Save view
          </PopoverTrigger>
          <PopoverContent className={styles.panel} align="end">
            <form
              className={styles.editor}
              onSubmit={(e) => {
                e.preventDefault();
                save();
              }}
            >
              <div className={styles.nameField}>
                <Input
                  label="Name this view"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={40}
                  placeholder="Westlands mornings"
                />
              </div>
              <p className={styles.muted}>It keeps the filters on screen. Only you see it, in this browser.</p>
              <div className={styles.editorFoot}>
                <span />
                <span className={styles.buttons}>
                  <Button variant="ghost" size="sm" type="button" onClick={() => setNaming(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" type="submit" disabled={!name.trim()}>
                    Save
                  </Button>
                </span>
              </div>
            </form>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}
